/**
 * Printing.
 *
 * Receipts and labels are built as HTML and printed through a hidden frame at
 * the exact paper size. That is deliberate: many cheap thermal printers mangle
 * Arabic when they are sent raw text - letters come out separated and in the
 * wrong order. Letting the browser lay the Arabic out and sending the printer a
 * rendered page avoids the problem completely, and it works with no internet
 * because the fonts are bundled on the device.
 *
 * A shop that prints through the print helper (Settings -> Printing) gets the
 * same HTML drawn as a picture and sent straight to the printer it chose, with
 * no print window - see printing.ts. Everything here decides nothing about that
 * except to hand receipts and labels over when the shop has chosen it.
 */
import JsBarcode from 'jsbarcode';
import { notifyPrintProblem, printLabelsViaHelper, printRollViaHelper, printRoute } from './printing.js';

const SVG_NS = 'http://www.w3.org/2000/svg';

/** A barcode as an inline SVG string, so it prints crisply at any size. */
export function barcodeSvg(
  value: string,
  opts: { height?: number; width?: number; showText?: boolean; fontSize?: number; format?: string } = {},
): string {
  try {
    const svg = document.createElementNS(SVG_NS, 'svg');
    JsBarcode(svg, value, {
      format: opts.format ?? (/^\d{13}$/.test(value) ? 'EAN13' : 'CODE128'),
      height: opts.height ?? 40,
      width: opts.width ?? 1.6,
      displayValue: opts.showText ?? true,
      fontSize: opts.fontSize ?? 12,
      margin: 0,
      textMargin: 1,
      font: 'Inter, sans-serif',
      background: '#ffffff',
      lineColor: '#000000',
    });
    return svg.outerHTML;
  } catch {
    // An invalid code must never crash the till; show the number instead.
    return `<div style="font:600 11px monospace">${escapeHtml(value)}</div>`;
  }
}

export function escapeHtml(s: unknown): string {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

interface PrintOpts {
  /** Paper width in mm. 80 for a receipt roll; a label uses its own size. */
  widthMm?: number;
  heightMm?: number;
  title?: string;
  dir?: 'rtl' | 'ltr';
  lang?: string;
  /** Open the cash drawer with this print, where the shop prints through the helper. */
  kick?: boolean;
}

/**
 * The whole page a piece of printing is printed in. Also what an on-screen
 * preview shows, so the preview is the paper and not an imitation of it.
 */
export function printPage(bodyHtml: string, opts: PrintOpts = {}): string {
  const page = opts.widthMm
    ? `@page { size: ${opts.widthMm}mm ${opts.heightMm ? `${opts.heightMm}mm` : 'auto'}; margin: 0; }`
    : '@page { size: A4; margin: 12mm; }';
  return `<!doctype html>
<html lang="${opts.lang ?? document.documentElement.lang}" dir="${opts.dir ?? document.documentElement.dir}">
<head>
<meta charset="utf-8">
<title>${escapeHtml(opts.title ?? 'Print')}</title>
<link rel="stylesheet" href="/fonts/fonts.css">
<style>
  ${page}
  * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  body {
    margin: 0;
    font-family: 'Cairo', 'Inter', system-ui, sans-serif;
    color: #000;
    background: #fff;
    ${opts.widthMm ? `width: ${opts.widthMm}mm;` : ''}
  }
  .tnum { font-variant-numeric: tabular-nums; }
</style>
</head>
<body>${bodyHtml}</body>
</html>`;
}

/**
 * Print a piece of HTML. A receipt-roll print goes to the shop's receipt printer
 * through the helper when the shop has chosen that; anything else, or if the
 * helper does not answer, through the browser's print window.
 */
export function printHtml(bodyHtml: string, opts: PrintOpts = {}): Promise<void> {
  const r = printRoute();
  if (r.viaHelper && r.receiptPrinter && opts.widthMm === 80 && !opts.heightMm) {
    return printRollViaHelper(bodyHtml, { kick: opts.kick, dir: opts.dir, lang: opts.lang }).catch((e) => {
      notifyPrintProblem(e);
      return printInWindow(bodyHtml, opts);
    });
  }
  return printInWindow(bodyHtml, opts);
}

/**
 * The browser's own print window. Everything happens in a hidden frame so the
 * till screen never flickers and the cashier never loses their place.
 */
function printInWindow(bodyHtml: string, opts: PrintOpts = {}): Promise<void> {
  return new Promise((resolve) => {
    const frame = document.createElement('iframe');
    frame.setAttribute('aria-hidden', 'true');
    frame.style.cssText = 'position:fixed;inset-block-start:-10000px;inset-inline-start:-10000px;width:0;height:0;border:0';
    document.body.appendChild(frame);

    const doc = frame.contentDocument!;
    doc.open();
    doc.write(printPage(bodyHtml, opts));
    doc.close();

    const go = () => {
      try {
        frame.contentWindow?.focus();
        frame.contentWindow?.print();
      } finally {
        // Give the print dialog time to take its snapshot before tidying up.
        setTimeout(() => {
          frame.remove();
          resolve();
        }, 1500);
      }
    };

    // Wait for the bundled fonts, or Arabic prints in a fallback face.
    const fonts = (frame.contentDocument as Document & { fonts?: FontFaceSet }).fonts;
    if (fonts?.ready) {
      void fonts.ready.then(() => setTimeout(go, 120));
    } else {
      setTimeout(go, 400);
    }
  });
}

// ---------------------------------------------------------------------------
// Receipts - 80 mm thermal
// ---------------------------------------------------------------------------

export interface ReceiptLine {
  nameEn: string;
  nameAr: string;
  size: string;
  colorEn?: string;
  colorAr?: string;
  qty: number;
  unitPrice: number;
  net: number;
  discount?: number;
  /** The promotion this line took part in, printed under it when it took something off. */
  promo?: { nameEn: string; nameAr: string; amount: number } | null;
}

export interface ReceiptData {
  receiptNo: string;
  createdAt: string;
  cashierName: string;
  kind?: string;
  lines: ReceiptLine[];
  gross: number;
  /** Promotions and discounts together; promoTotal says how much of it was promotions. */
  discountTotal: number;
  promoTotal?: number;
  total: number;
  rounding: number;
  payable: number;
  vatAmount: number;
  netAmount: number;
  vatRate: number;
  changeIls: number;
  /**
   * How it was paid, as the METHOD - never as a finished label. The screen and
   * the receipt are in different languages: the till follows the cashier, the
   * receipt follows the shop's own Receipt language setting. Handing a label in
   * here printed the payment line in the cashier's language while every other
   * word on the paper obeyed the setting, so an Arabic-only receipt said "Cash".
   */
  payments: {
    method: string;
    /** the owner's own name for a custom method ("Jawwal Pay"); it has no translation */
    methodLabel?: string | null;
    currency: string;
    amount: number;
    amountIls: number;
    rate?: number;
  }[];
  customerName?: string | null;
  creditIssued?: { code: string; amount: number } | null;
  note?: string | null;
}

export interface ReceiptSettings {
  storeNameEn: string;
  storeNameAr: string;
  address: string;
  phone: string;
  taxNumber: string;
  logoDataUrl?: string | null;
  receiptLang: 'en' | 'ar' | 'both';
  receiptFooterEn: string;
  receiptFooterAr: string;
  exchangeWindowDays: number;
}

const fmt = (agorot: number, symbol = '₪', decimals = 2): string => {
  const neg = agorot < 0;
  const abs = Math.abs(Math.round(agorot));
  const d = 10 ** decimals;
  const s = `${Math.floor(abs / d)}.${String(abs % d).padStart(decimals, '0')}`;
  return `${neg ? '-' : ''}${symbol}${s}`;
};

/**
 * What to call a payment method ON PAPER.
 *
 * The receipt keeps its own words, as this file does for Discount, Total and
 * Change, because the receipt language is the SHOP'S setting and the interface
 * language is the cashier's. Looking these up through the app's translator
 * would print whatever the till happens to be set to.
 *
 * `tab` is money the customer owes the shop and `credit` is money the shop owes
 * the customer. They are opposites, and a receipt that swaps them tells the
 * customer the wrong thing about their own money.
 */
function paymentName(method: string, methodLabel: string | null | undefined, showAr: boolean): string {
  switch (method) {
    case 'cash': return showAr ? 'نقداً' : 'Cash';
    case 'card': return showAr ? 'بطاقة' : 'Card';
    case 'credit': return showAr ? 'رصيد المحل' : 'Store credit';
    case 'gift': return showAr ? 'بطاقة هدية' : 'Gift card';
    case 'tab': return showAr ? 'على الحساب' : 'On account';
    case 'other': return methodLabel || (showAr ? 'غير ذلك' : 'Other');
    // A method added later prints as itself rather than borrowing another's name.
    default: return methodLabel || method;
  }
}

/**
 * @param gift  A gift receipt shows the items and the same scannable receipt
 *              barcode, but no prices at all - so the person who received the
 *              present never learns what it cost.
 */
export function receiptHtml(d: ReceiptData, s: ReceiptSettings, gift = false): string {
  const showEn = s.receiptLang === 'en' || s.receiptLang === 'both';
  const showAr = s.receiptLang === 'ar' || s.receiptLang === 'both';

  const both = (en: string, ar: string) =>
    [showAr ? `<div class="ar">${escapeHtml(ar)}</div>` : '', showEn ? `<div class="en">${escapeHtml(en)}</div>` : '']
      .filter(Boolean)
      .join('');

  const lineRows = d.lines
    .map((l) => {
      const name = [showAr ? l.nameAr : '', showEn ? l.nameEn : ''].filter(Boolean).join(' / ');
      const detail = [l.size, showAr ? l.colorAr : l.colorEn].filter(Boolean).join(' · ');
      const promo =
        l.promo && l.promo.amount > 0 && !gift
          ? `<div class="d">${escapeHtml(showAr ? l.promo.nameAr || l.promo.nameEn : l.promo.nameEn || l.promo.nameAr)} <span class="tnum">-${fmt(l.promo.amount)}</span></div>`
          : '';
      return `<tr class="line">
        <td class="nm">
          <div class="n">${escapeHtml(name)}</div>
          <div class="d">${escapeHtml(detail)}</div>
          ${promo}
        </td>
        <td class="q tnum">${l.qty}</td>
        ${gift ? '' : `<td class="p tnum">${fmt(l.unitPrice)}</td><td class="t tnum">${fmt(l.net)}</td>`}
      </tr>`;
    })
    .join('');

  const totals = gift
    ? ''
    : `
    <table class="totals">
      ${d.promoTotal ? `<tr><td>${showAr ? 'العروض' : 'Promotions'}</td><td class="tnum">-${fmt(d.promoTotal)}</td></tr>` : ''}
      ${d.discountTotal - (d.promoTotal ?? 0) ? `<tr><td>${showAr ? 'الخصم' : 'Discount'}</td><td class="tnum">-${fmt(d.discountTotal - (d.promoTotal ?? 0))}</td></tr>` : ''}
      ${d.rounding ? `<tr><td>${showAr ? 'تقريب' : 'Rounding'}</td><td class="tnum">${fmt(d.rounding)}</td></tr>` : ''}
      <tr class="grand"><td>${showAr ? 'المجموع' : 'Total'}</td><td class="tnum">${fmt(d.payable)}</td></tr>
      <tr class="vat"><td>${showAr ? `<bdi dir="rtl">شامل ضريبة ${d.vatRate}%</bdi>` : `Includes VAT ${d.vatRate}%`}</td><td class="tnum">${fmt(d.vatAmount)}</td></tr>
      <tr class="vat"><td>${showAr ? 'قبل الضريبة' : 'Before VAT'}</td><td class="tnum">${fmt(d.netAmount)}</td></tr>
    </table>

    <table class="pay">
      ${d.payments
        .map(
          (p) => `<tr>
            <td>${escapeHtml(paymentName(p.method, p.methodLabel, showAr))}${p.currency !== 'ILS' ? ` (${escapeHtml(p.currency)}${p.rate ? ` @ ${p.rate.toFixed(3)}` : ''})` : ''}</td>
            <td class="tnum">${p.currency === 'ILS' ? fmt(p.amount) : `${fmt(p.amount, '', p.currency === 'JOD' ? 3 : 2)} ${escapeHtml(p.currency)}`}</td>
            ${p.currency !== 'ILS' ? `<td class="tnum muted">${fmt(p.amountIls)}</td>` : '<td></td>'}
          </tr>`,
        )
        .join('')}
      ${d.changeIls ? `<tr class="change"><td>${showAr ? 'الباقي' : 'Change'}</td><td class="tnum">${fmt(d.changeIls)}</td><td></td></tr>` : ''}
    </table>

    ${
      d.creditIssued
        ? `<div class="credit">
             ${both('Store credit issued', 'رصيد محل صادر')}
             <div class="big tnum">${fmt(d.creditIssued.amount)}</div>
             ${barcodeSvg(d.creditIssued.code, { height: 30, width: 1.4, fontSize: 10 })}
           </div>`
        : ''
    }`;

  return `
<style>
  body { width: 80mm; padding: 3mm 4mm 6mm; font-size: 11px; line-height: 1.5; }
  .center { text-align: center; }
  .store { font-size: 15px; font-weight: 700; }
  .store .ar { font-size: 16px; }
  .meta { font-size: 10px; }
  .logo { max-width: 38mm; max-height: 18mm; margin: 0 auto 2mm; display: block; }
  hr { border: none; border-top: 1px dashed #000; margin: 2mm 0; }
  table { width: 100%; border-collapse: collapse; }
  .items th { font-size: 9px; text-align: start; border-bottom: 1px solid #000; padding-bottom: 1mm; font-weight: 600; }
  .items td { padding: 1mm 0; vertical-align: top; }
  .items .nm .n { font-weight: 600; }
  .items .nm .d { font-size: 9px; }
  .items .q { text-align: center; width: 8mm; }
  .items .p, .items .t { text-align: end; width: 16mm; }
  .totals { margin-top: 2mm; }
  .totals td { padding: 0.6mm 0; }
  .totals td:last-child { text-align: end; }
  .totals .grand td { font-size: 15px; font-weight: 700; border-top: 1px solid #000; border-bottom: 1px solid #000; padding: 1.2mm 0; }
  .totals .vat td { font-size: 9px; }
  .pay { margin-top: 2mm; font-size: 10px; }
  .pay td { padding: 0.4mm 0; }
  .pay td:nth-child(2) { text-align: end; }
  .pay td:nth-child(3) { text-align: end; width: 16mm; }
  .pay .change td { font-weight: 700; font-size: 12px; }
  .muted { opacity: 0.7; font-size: 9px; }
  .credit { text-align: center; border: 1px dashed #000; padding: 2mm; margin-top: 3mm; }
  .credit .big { font-size: 16px; font-weight: 700; }
  .policy { font-size: 9px; margin-top: 3mm; text-align: center; }
  .gift { text-align: center; font-weight: 700; border: 1px solid #000; padding: 1.5mm; margin-bottom: 2mm; }
  .barcode { text-align: center; margin-top: 3mm; }
  .thanks { text-align: center; font-weight: 600; margin-top: 2mm; }
</style>

${gift ? `<div class="gift">${both('Gift receipt', 'فاتورة هدية')}</div>` : ''}

<div class="center">
  ${s.logoDataUrl ? `<img class="logo" src="${s.logoDataUrl}" alt="">` : ''}
  <div class="store">${both(s.storeNameEn, s.storeNameAr)}</div>
  ${s.address ? `<div class="meta">${escapeHtml(s.address)}</div>` : ''}
  ${s.phone ? `<div class="meta" dir="ltr">${escapeHtml(s.phone)}</div>` : ''}
  ${s.taxNumber ? `<div class="meta">${showAr ? 'الرقم الضريبي' : 'VAT no.'} ${escapeHtml(s.taxNumber)}</div>` : ''}
</div>

<hr>

<table class="meta">
  <tr><td>${showAr ? 'رقم الفاتورة' : 'Receipt'}</td><td style="text-align:end"><strong>${escapeHtml(d.receiptNo)}</strong></td></tr>
  <tr><td>${showAr ? 'التاريخ' : 'Date'}</td><td style="text-align:end" class="tnum">${new Date(d.createdAt).toLocaleString('en-GB')}</td></tr>
  <tr><td>${showAr ? 'خدمكم' : 'Served by'}</td><td style="text-align:end">${escapeHtml(d.cashierName)}</td></tr>
  ${d.customerName ? `<tr><td>${showAr ? 'الزبون' : 'Customer'}</td><td style="text-align:end">${escapeHtml(d.customerName)}</td></tr>` : ''}
</table>

<hr>

<table class="items">
  <thead>
    <tr>
      <th>${showAr ? 'الصنف' : 'Item'}</th>
      <th style="text-align:center">${showAr ? 'كمية' : 'Qty'}</th>
      ${gift ? '' : `<th style="text-align:end">${showAr ? 'السعر' : 'Price'}</th><th style="text-align:end">${showAr ? 'المجموع' : 'Total'}</th>`}
    </tr>
  </thead>
  <tbody>${lineRows}</tbody>
</table>

${totals}

<div class="policy">
  ${showAr ? `<div>${escapeHtml(s.receiptFooterAr)}</div>` : ''}
  ${showEn ? `<div>${escapeHtml(s.receiptFooterEn)}</div>` : ''}
</div>

<div class="barcode">
  ${barcodeSvg(d.receiptNo, { height: 34, width: 1.5, fontSize: 11, format: 'CODE128' })}
  <div class="meta">${showAr ? 'امسح هذا الباركود للاستبدال' : 'Scan this for an exchange'}</div>
</div>

<div class="thanks">${both('Thank you', 'شكراً لزيارتكم')}</div>
`;
}

export function printReceipt(d: ReceiptData, s: ReceiptSettings, gift = false, opts: { kick?: boolean } = {}): Promise<void> {
  return printHtml(receiptHtml(d, s, gift), {
    widthMm: 80,
    title: `${gift ? 'Gift ' : ''}Receipt ${d.receiptNo}`,
    kick: opts.kick,
  });
}

/**
 * "No sale": a slip whose only job is to make the receipt printer open the
 * drawer, for a till whose drawer opens with the printer. It says who opened
 * it and when, so the slip in the drawer matches the entry in the log.
 */
export function printNoSale(s: ReceiptSettings, who: string): Promise<void> {
  const when = new Date().toLocaleString(s.receiptLang === 'en' ? 'en-GB' : 'ar-PS-u-nu-latn');
  const store = s.receiptLang === 'en' ? s.storeNameEn || s.storeNameAr : s.storeNameAr || s.storeNameEn;
  const html = `
<style>body { width: 80mm; padding: 3mm 4mm 6mm; font-size: 12px; text-align: center; } .big { font-size: 18px; font-weight: 700; margin: 2mm 0; }</style>
<div>${escapeHtml(store)}</div>
<div class="big">${s.receiptLang === 'en' ? 'NO SALE' : s.receiptLang === 'ar' ? 'فتح الدرج - بدون بيع' : 'فتح الدرج - بدون بيع<br>NO SALE'}</div>
<div>${escapeHtml(who)}</div>
<div>${escapeHtml(when)}</div>`;
  return printHtml(html, { widthMm: 80, title: 'No sale' });
}

// ---------------------------------------------------------------------------
// Labels
// ---------------------------------------------------------------------------

export interface LabelConfig {
  showStoreName: boolean;
  showNameEn: boolean;
  showNameAr: boolean;
  showSize: boolean;
  showColor: boolean;
  showPrice: boolean;
  showBarcodeText: boolean;
  fontScale: number;
}

export interface LabelItem {
  nameEn: string;
  nameAr: string;
  size: string;
  colorEn?: string;
  colorAr?: string;
  price: number;
  barcode: string;
}

export const DEFAULT_LABEL_CONFIG: LabelConfig = {
  showStoreName: true,
  showNameEn: true,
  showNameAr: true,
  showSize: true,
  showColor: true,
  showPrice: true,
  showBarcodeText: true,
  fontScale: 1,
};

export function labelHtml(
  item: LabelItem,
  cfg: LabelConfig,
  size: { widthMm: number; heightMm: number },
  storeName: string,
): string {
  const f = cfg.fontScale;
  // Small labels need a shorter barcode so the text still fits underneath.
  const barHeight = Math.max(14, Math.round(size.heightMm * 0.9));
  return `<div class="label" style="width:${size.widthMm}mm;height:${size.heightMm}mm">
    ${cfg.showStoreName ? `<div class="store" style="font-size:${6 * f}pt">${escapeHtml(storeName)}</div>` : ''}
    ${cfg.showNameAr ? `<div class="nm ar" style="font-size:${7.5 * f}pt">${escapeHtml(item.nameAr)}</div>` : ''}
    ${cfg.showNameEn ? `<div class="nm" style="font-size:${7 * f}pt">${escapeHtml(item.nameEn)}</div>` : ''}
    <div class="row" style="font-size:${7 * f}pt">
      ${cfg.showSize ? `<span class="sz">${escapeHtml(item.size)}</span>` : ''}
      ${cfg.showColor && item.colorEn ? `<span class="cl">${escapeHtml(item.colorAr || item.colorEn)}</span>` : ''}
      ${cfg.showPrice ? `<span class="pr" style="font-size:${9.5 * f}pt">${fmt(item.price)}</span>` : ''}
    </div>
    <div class="bc">${barcodeSvg(item.barcode, {
      height: barHeight,
      width: 1.15,
      showText: cfg.showBarcodeText,
      fontSize: 8 * f,
    })}</div>
  </div>`;
}

export function printLabels(
  labels: { item: LabelItem; qty: number }[],
  cfg: LabelConfig,
  size: { widthMm: number; heightMm: number },
  storeName: string,
): Promise<void> {
  const all = labels.flatMap(({ item, qty }) => Array.from({ length: Math.max(0, qty) }, () => item));
  const style = `
<style>
  body { width: ${size.widthMm}mm; }
  .label {
    display: flex; flex-direction: column; justify-content: space-between;
    align-items: center; text-align: center;
    padding: 1mm 1.5mm; overflow: hidden;
    page-break-after: always; break-after: page;
  }
  .label:last-child { page-break-after: auto; break-after: auto; }
  .store { font-weight: 600; letter-spacing: 0.02em; }
  .nm { font-weight: 600; line-height: 1.15; max-height: 2.4em; overflow: hidden; }
  .row { display: flex; align-items: baseline; gap: 1.5mm; justify-content: center; flex-wrap: wrap; }
  .sz { font-weight: 700; }
  .pr { font-weight: 700; }
  .bc svg { max-width: 100%; }
</style>`;
  const body = style + all.map((item) => labelHtml(item, cfg, size, storeName)).join('');

  // Through the helper: one picture per different label, and how many of it.
  const r = printRoute();
  if (r.viaHelper && r.labelPrinter) {
    const pages = labels.map(({ item, qty }) => ({ html: style + labelHtml(item, cfg, size, storeName), count: qty }));
    return printLabelsViaHelper(pages, size).catch((e) => {
      notifyPrintProblem(e);
      return printInWindow(body, { widthMm: size.widthMm, heightMm: size.heightMm, title: `${all.length} labels` });
    });
  }

  return printHtml(body, {
    widthMm: size.widthMm,
    heightMm: size.heightMm,
    title: `${all.length} labels`,
  });
}

// ---------------------------------------------------------------------------
// A4 documents: the Z report and any report the owner wants on paper or as PDF
// ---------------------------------------------------------------------------

export function printDocument(title: string, bodyHtml: string): Promise<void> {
  return printHtml(
    `<style>
       body { font-size: 11pt; }
       h1 { font-size: 16pt; margin: 0 0 2mm; }
       .sub { color: #555; font-size: 9pt; margin-bottom: 6mm; }
       table { width: 100%; border-collapse: collapse; margin-bottom: 6mm; }
       th { text-align: start; font-size: 8.5pt; text-transform: uppercase; letter-spacing: 0.04em;
            color: #555; border-bottom: 1.5px solid #000; padding: 2mm 1.5mm; }
       td { padding: 1.8mm 1.5mm; border-bottom: 0.5px solid #ccc; font-size: 10pt; }
       td.num, th.num { text-align: end; font-variant-numeric: tabular-nums; }
       tfoot td { font-weight: 700; border-top: 1.5px solid #000; border-bottom: none; }
       .cards { display: flex; flex-wrap: wrap; gap: 4mm; margin-bottom: 6mm; }
       .kpi { border: 1px solid #ccc; border-radius: 2mm; padding: 3mm 4mm; min-width: 40mm; }
       .kpi .k { font-size: 8.5pt; color: #555; }
       .kpi .v { font-size: 14pt; font-weight: 700; font-variant-numeric: tabular-nums; }
       .foot { margin-top: 8mm; font-size: 8.5pt; color: #555; border-top: 0.5px solid #ccc; padding-top: 2mm; }
       @media print { .kpi { break-inside: avoid; } tr { break-inside: avoid; } }
     </style>
     ${bodyHtml}`,
    { title },
  );
}
